import React from 'react';
import { Check, Lock, Sparkles, Wind } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { usePageDesignPatterns } from '../hooks/use-page-design-patterns';
/**
 * Gallery of animated background patterns. Each tile is a small live preview:
 * the actual pattern renders inside the tile (scaled down) so the user sees
 * exactly the motion they'll get on the public page.
 *
 * Selection is independent from the theme picker — picking a pattern writes
 * to `Campaign.backgroundPattern` (override). Picking the "Theme default"
 * tile clears the override so the theme's declared pattern wins again.
 */
export function BackgroundPatternPicker({ selectedSlug, themeDefaultSlug, proUnlocked, activeDesign, onPick, disabled, }) {
    const { t } = useTranslation('campaign');
    const { data: patterns = [], isPending } = usePageDesignPatterns();
    const tintStyle = React.useMemo(() => {
        const colors = activeDesign?.tokens.colors ?? {};
        return {
            // Map theme colors to the same CSS vars the public templates use,
            // so the preview tiles render with the chosen theme's palette.
            ['--primary']: colors.primary ?? '#6366f1',
            ['--primary-soft']: colors.accent ?? colors.primary ?? '#a78bfa',
            ['--bg']: colors.background ?? '#0a0a0a',
            ['--fg']: colors.foreground ?? '#f5f5f5',
            ['--accent']: colors.accent ?? '#a78bfa',
        };
    }, [activeDesign]);
    const grouped = React.useMemo(() => {
        const groups = {};
        for (const p of patterns) {
            (groups[p.group] ??= []).push(p);
        }
        return groups;
    }, [patterns]);
    if (isPending) {
        return (<div className="grid gap-2 sm:grid-cols-3 lg:grid-cols-4">
                {Array.from({ length: 8 }).map((_, i) => (<Skeleton key={i} className="aspect-square w-full"/>))}
            </div>);
    }
    return (<div className="space-y-4" style={tintStyle}>
            {/* Theme-default tile sits on its own — clearing the override is the most
            common action so it gets the prime slot. */}
            <div className="flex items-center gap-2">
                <button type="button" onClick={() => !disabled && onPick(null)} disabled={disabled} aria-pressed={!selectedSlug} className={cn('inline-flex items-center gap-2 rounded-md border px-3 py-2 text-xs font-medium transition-colors', !selectedSlug
            ? 'border-primary bg-primary/10 text-primary'
            : 'border-border bg-muted/40 text-muted-foreground hover:border-primary/60')}>
                    {!selectedSlug && <Check className="h-3.5 w-3.5"/>}
                    {t('background_pattern.use_theme_default', { defaultValue: 'Use theme default' })}
                    {themeDefaultSlug && (<span className="ml-1 rounded bg-background/60 px-1.5 py-0.5 text-[10px] uppercase tracking-wider text-muted-foreground">
                            {themeDefaultSlug}
                        </span>)}
                </button>
            </div>

            {Object.entries(grouped).map(([group, items]) => (<section key={group} className="space-y-2">
                    <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                        {t(`background_pattern.group.${group}`, { defaultValue: group })}
                    </h4>
                    <div className="grid gap-2 sm:grid-cols-3 lg:grid-cols-4">
                        {items.map((pattern) => {
                const locked = pattern.proOnly && !proUnlocked;
                const selected = selectedSlug === pattern.slug;
                return (<button key={pattern.slug} type="button" onClick={() => !locked && !disabled && onPick(pattern.slug)} disabled={disabled || locked} aria-pressed={selected} aria-disabled={locked} className={cn('group relative aspect-square overflow-hidden rounded-md border text-left transition-all', selected
                        ? 'border-primary ring-1 ring-primary'
                        : 'border-border hover:border-primary/60 hover:shadow-sm', locked && 'cursor-not-allowed opacity-60')} title={pattern.label}>
                                    {/* Live mini-preview — same CSS that runs on the
                        public page, scaled to a 100×100 tile. */}
                                    <div className="bg-anim absolute inset-0 overflow-hidden" data-pattern={pattern.slug} aria-hidden="true" style={{
                        background: 'var(--bg)',
                    }}>
                                        {patternSpans(pattern.slug)}
                                    </div>

                                    {/* Label + badges on top */}
                                    <div className="absolute inset-x-0 bottom-0 flex items-center justify-between gap-1 bg-gradient-to-t from-black/70 to-transparent px-2 py-1.5">
                                        <span className="truncate text-[11px] font-medium text-white">
                                            {pattern.label}
                                        </span>
                                        {pattern.intensity === 'wow' && (<Sparkles className="h-3 w-3 shrink-0 text-amber-300" aria-label="WOW pattern"/>)}
                                        {pattern.intensity === 'calm' && (<Wind className="h-3 w-3 shrink-0 text-cyan-300/80" aria-label="Calm pattern"/>)}
                                    </div>

                                    {locked && (<span className="absolute right-1.5 top-1.5 inline-flex items-center gap-1 rounded-full bg-muted/90 px-1.5 py-0.5 text-[9px] font-medium uppercase tracking-wide text-muted-foreground backdrop-blur-sm">
                                            <Lock className="h-2.5 w-2.5"/>
                                            {t('mode_picker.lock_badge', { defaultValue: 'Solo+' })}
                                        </span>)}
                                    {selected && !locked && (<span className="absolute right-1.5 top-1.5 inline-flex h-5 w-5 items-center justify-center rounded-full bg-primary text-primary-foreground">
                                            <Check className="h-3 w-3"/>
                                        </span>)}
                                </button>);
            })}
                    </div>
                </section>))}
        </div>);
}
/**
 * Emit the right number of <span> children for patterns that need DOM nodes.
 * Mirror of the Twig template's spanCounts map — kept in lockstep so previews
 * match the public page exactly.
 */
function patternSpans(slug) {
    const counts = {
        bokeh: 3,
        swirl: 5,
        fireworks: 8,
        firefly: 10,
        petals: 14,
        starfield: 30,
        rain: 14,
        snow: 15,
        bubbles: 10,
        constellation: 6,
        'lava-lamp': 4,
        sparkle: 14,
        'matrix-rain': 11,
        confetti: 15,
        'data-stream': 12,
        'iso-cubes': 6,
    };
    if (slug === 'floating-code') {
        // Snippet text shows up at the tile's tiny size as a green smudge —
        // that's fine for a preview; the live page is what shows real lines.
        const snippets = [
            'const x = 42',
            'function f() {',
            '$ git push',
            'useEffect()',
            'return data',
            '// TODO',
            'npm run build',
            'if (ok) {',
            'export App',
            'READY 1.2s',
        ];
        return snippets.map((text, i) => <span key={i}>{text}</span>);
    }
    const n = counts[slug] ?? 0;
    return Array.from({ length: n }, (_, i) => <span key={i}/>);
}
